import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useBoardChartData } from '../../hooks/useBoardChartData';
import { BaseChartProps } from './types';
import { CHART_COLORS, CHART_STYLES, getCurrencySymbol, formatCompactNumber, formatCurrency } from './chartTheme';
import { useSetChartMetadata } from './ChartContext';
import { parseISO, getDay } from 'date-fns';
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Cell,
} from 'recharts';

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export const SalesByDayOfWeekChart: React.FC<BaseChartProps> = () => {
    const { t } = useTranslation();
    const { finance, currencySettings } = useBoardChartData();

    const currencySymbol = useMemo(
        () => getCurrencySymbol(currencySettings?.default),
        [currencySettings?.default]
    );

    const chartData = useMemo(() => {
        if (!finance?.dailyBreakdown) return [];

        const totals: number[] = [0, 0, 0, 0, 0, 0, 0];
        const counts: number[] = [0, 0, 0, 0, 0, 0, 0];

        for (const [date, day] of Object.entries(finance.dailyBreakdown)) {
            const dayOfWeek = getDay(parseISO(date));
            const metrics = (day as Record<string, unknown>).metrics as Record<string, number> | undefined;
            const revenue = metrics?.revenue ?? 0;
            totals[dayOfWeek] += revenue;
            counts[dayOfWeek]++;
        }

        // Reorder to Mon-Sun
        const reorder = [1, 2, 3, 4, 5, 6, 0];
        const data = reorder.map((i) => ({
            day: DAY_LABELS[i],
            avgRevenue: counts[i] > 0 ? Math.round((totals[i] / counts[i]) * 100) / 100 : 0,
        }));

        return data;
    }, [finance?.dailyBreakdown]);

    const maxRevenue = useMemo(
        () => Math.max(...chartData.map((d) => d.avgRevenue), 0),
        [chartData]
    );

    const isEmpty = useMemo(
        () => chartData.length === 0 || chartData.every((d) => d.avgRevenue === 0),
        [chartData]
    );

    useSetChartMetadata({ isEmpty });

    return (
        <div className="w-full h-full bg-transparent overflow-hidden flex flex-col">
            <div className="flex-1 min-h-0 p-2">
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                        data={chartData}
                        margin={CHART_STYLES.margin.withAxis}
                    >
                        <CartesianGrid
                            vertical={false}
                            stroke={CHART_STYLES.grid.stroke}
                            strokeDasharray={CHART_STYLES.grid.strokeDasharray}
                            strokeWidth={CHART_STYLES.grid.strokeWidth}
                            opacity={CHART_STYLES.grid.opacity}
                        />
                        <XAxis
                            dataKey="day"
                            axisLine={CHART_STYLES.axis.axisLine}
                            tickLine={CHART_STYLES.axis.tickLine}
                            tick={CHART_STYLES.axis.tick}
                            tickMargin={CHART_STYLES.axis.tickMargin}
                        />
                        <YAxis
                            axisLine={CHART_STYLES.axis.axisLine}
                            tickLine={CHART_STYLES.axis.tickLine}
                            tick={CHART_STYLES.axis.tick}
                            tickFormatter={(v: number) => formatCompactNumber(v, currencySymbol)}
                        />
                        <Tooltip
                            contentStyle={CHART_STYLES.tooltip.contentStyle}
                            cursor={CHART_STYLES.tooltip.cursor}
                            formatter={(value: number) => [
                                formatCurrency(value, currencySymbol),
                                t('charts.avgRevenue', 'Avg Revenue'),
                            ]}
                        />
                        <Bar
                            dataKey="avgRevenue"
                            name={t('charts.avgRevenue', 'Avg Revenue')}
                            radius={CHART_STYLES.bar.radius}
                            maxBarSize={CHART_STYLES.bar.maxBarSize}
                        >
                            {chartData.map((entry, index) => {
                                const intensity = maxRevenue > 0
                                    ? 0.3 + (entry.avgRevenue / maxRevenue) * 0.7
                                    : 0.5;
                                return (
                                    <Cell
                                        key={`cell-${index}`}
                                        fill={CHART_COLORS.primary}
                                        fillOpacity={intensity}
                                    />
                                );
                            })}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};
